import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addressService, cartService, categoryService, couponService, invoiceService, orderService, productService, wishlistService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function CustomerShop() {
  const { isAuthenticated } = useAuth();
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [message, setMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await categoryService.index();
      setCategories(data.data || data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadCustomerData();
    }
  }, [isAuthenticated]);

  const loadProducts = async () => {
    const data = await productService.index({ active_only: 1 });
    setProducts(Array.isArray(data) ? data : data.data || []);
  };

  const loadCustomerData = async () => {
    const [cartData, wishlistData, ordersData, invoicesData, addressData] = await Promise.all([
      cartService.index(),
      wishlistService.index(),
      orderService.index(),
      invoiceService.index(),
      addressService.index(),
    ]);

    setCart(cartData);
    setWishlist(Array.isArray(wishlistData) ? wishlistData : wishlistData.data || []);
    setOrders(Array.isArray(ordersData) ? ordersData : ordersData.data || []);
    setInvoices(Array.isArray(invoicesData) ? invoicesData : invoicesData.data || []);
    setAddresses(addressData || []);
  };

  const viewProduct = async (productId) => {
    const data = await productService.show(productId);
    setSelectedProduct(data);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      setMessage('Please login to submit a review.');
      return;
    }
    try {
      await productService.storeReview(selectedProduct.id, reviewForm);
      setReviewForm({ rating: 5, comment: '' });
      setMessage('Review submitted.');
      await viewProduct(selectedProduct.id);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Review submission failed.');
    }
  };

  const addToCart = async (productId) => {
    if (!isAuthenticated) {
      setMessage('Please login to add products to cart.');
      return;
    }
    const data = await cartService.addItem(productId, 1);
    setCart(data);
    setMessage('Product added to cart.');
  };

  const addToWishlist = async (productId) => {
    if (!isAuthenticated) {
      setMessage('Please login to add products to wishlist.');
      return;
    }
    await wishlistService.store(productId);
    const data = await wishlistService.index();
    setWishlist(data.data || []);
    setMessage('Product added to wishlist.');
  };

  const removeFromWishlist = async (productId) => {
    try {
      await wishlistService.destroy(productId);
      const data = await wishlistService.index();
      setWishlist(data.data || data || []);
      setMessage('Product removed from wishlist.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Unable to remove from wishlist.');
    }
  };

  const updateCartItem = async (id, quantity) => {
    const data = await cartService.updateItem(id, quantity);
    setCart(data);
  };

  const removeCartItem = async (id) => {
    const data = await cartService.removeItem(id);
    setCart(data);
  };

  const validateCoupon = async () => {
    if (!couponCode) return;
    try {
      const result = await couponService.validate(couponCode, cart?.subtotal || 0);
      setDiscount(Number(result.discount || 0));
      setMessage(`Coupon applied. Discount: ₹${result.discount}`);
    } catch (error) {
      setDiscount(0);
      setMessage(error.response?.data?.message || 'Invalid coupon.');
    }
  };

  const checkout = async () => {
    const address = addresses[0];
    if (!address) {
      setMessage('Please add an address in your profile before checkout.');
      return;
    }

    try {
      await orderService.checkout({
        shipping_address_id: address.id,
        billing_address_id: address.id,
        payment_method: 'cod',
        coupon_code: couponCode || undefined,
      });
      setCouponCode('');
      setDiscount(0);
      setMessage('Order placed successfully.');
      await loadCustomerData();
      setTab('orders');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Checkout failed.');
    }
  };

  const cartItems = cart?.items || [];
  const cartTotal = Math.max(0, Number(cart?.total || 0) - discount);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const matchesStatus = orderStatusFilter === 'all' || order.status === orderStatusFilter;
      const matchesSearch = orderSearch.trim() === ''
        ? true
        : order.order_number?.toLowerCase().includes(orderSearch.trim().toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [orders, orderStatusFilter, orderSearch]);

  const orderStatusOptions = ['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'];

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-10 text-center">
          <p className="text-sm uppercase tracking-[0.3em] text-red-600 mb-3">Shop</p>
          <h1 className="text-4xl md:text-5xl font-black text-slate-900">Discover Our Products</h1>
          <p className="text-slate-500 mt-4 max-w-2xl mx-auto">Browse the Ortus portfolio, manage your cart, wishlist, and orders from a single streamlined workspace.</p>
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          {['products', 'cart', 'wishlist', 'checkout', 'orders', 'invoices'].map((item) => (
            <button
              key={item}
              onClick={() => setTab(item)}
              className={`px-4 py-2 rounded-md capitalize ${tab === item ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'}`}
            >
              {item}
            </button>
          ))}
        </div>

        {message && <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">{message}</div>}

        {tab === 'products' && (
          <div>
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full max-w-md border rounded p-2 mb-4"
            />
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setActiveCategory('')}
                className={`px-3 py-1 rounded text-sm ${activeCategory === '' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-3 py-1 rounded text-sm ${activeCategory === cat.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.filter((p) => {
                const matchesCategory = activeCategory === '' || Number(p.category?.id) === Number(activeCategory);
                const matchesSearch = searchQuery === '' || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || (p.description || '').toLowerCase().includes(searchQuery.toLowerCase());
                return matchesCategory && matchesSearch;
              }).map((product) => (
                <div key={product.id} className="bg-white rounded-lg shadow p-4">
                {product.featured_image ? (
                  <img src={product.featured_image} alt={product.name} className="h-36 w-full rounded mb-4 object-cover" />
                ) : (
                  <div className="h-36 bg-gray-100 rounded mb-4 flex items-center justify-center text-gray-400">Product</div>
                )}
                <h3 className="font-semibold text-lg">{product.name}</h3>
                <p className="text-sm text-gray-500 line-clamp-2">{product.description}</p>
                <div className="mt-3 font-bold">₹{product.discount_price || product.price}</div>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => addToCart(product.id)} className="flex-1 bg-blue-600 text-white px-3 py-2 rounded">Cart</button>
                  <button onClick={() => addToWishlist(product.id)} className="flex-1 bg-gray-200 text-gray-800 px-3 py-2 rounded">Wishlist</button>
                </div>
                <button onClick={() => viewProduct(product.id)} className="mt-2 w-full text-blue-600 text-sm">View Details</button>
              </div>
            ))}
            {products.length === 0 && <div className="text-gray-500">No products available yet.</div>}
            </div>
          </div>
        )}

        {tab === 'cart' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">Cart</h2>
            {cartItems.map((item) => (
              <div key={item.id} className="flex justify-between items-center border-b py-3">
                <div>
                  <div className="font-medium">{item.product?.name}</div>
                  <div className="text-sm text-gray-500">₹{item.price} x {item.quantity}</div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" min="1" value={item.quantity} onChange={(e) => updateCartItem(item.id, Number(e.target.value))} className="w-20 border rounded p-1" />
                  <button onClick={() => removeCartItem(item.id)} className="text-red-600">Remove</button>
                </div>
              </div>
            ))}
            {cartItems.length === 0 && <div className="text-gray-500">Your cart is empty.</div>}
            <div className="mt-6 text-right font-bold text-xl">Total: ₹{cart?.total || 0}</div>
          </div>
        )}

        {tab === 'wishlist' && (
          <div className="bg-white rounded-3xl shadow p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-black text-slate-900">Wishlist</h2>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{wishlist.length} items</p>
              </div>
              <p className="text-xs text-slate-500">Saved favorites for quick checkout</p>
            </div>

            {wishlist.length === 0 && (
              <div className="text-center py-8 border border-dashed rounded-2xl text-slate-500">
                No wishlist items. Tap the heart icon on any product to save it here.
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {wishlist.map((item) => (
                <div key={item.id} className="p-4 border border-slate-100 rounded-2xl flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{item.product?.name}</p>
                      <p className="text-sm text-slate-500">₹{item.product?.price}</p>
                    </div>
                    <button onClick={() => removeFromWishlist(item.product_id)} className="text-xs text-rose-500">Remove</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => addToCart(item.product_id)} className="flex-1 bg-red-600 text-white rounded-2xl py-2 font-semibold">Add to Cart</button>
                    <button onClick={() => viewProduct(item.product_id)} className="flex-1 border border-slate-200 rounded-2xl py-2 text-sm font-semibold text-slate-700">Details</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'checkout' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6 flex flex-col justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-4">Addresses</h2>
                {addresses.length > 0 ? (
                  <ul className="space-y-3">
                    {addresses.map((addr) => (
                      <li key={addr.id} className="border rounded-lg p-3 text-sm text-slate-600">
                        <p className="font-semibold text-slate-900">{addr.full_name}</p>
                        <p>{addr.address_line1 || addr.address_line_1}</p>
                        <p>{addr.city}, {addr.state}</p>
                        <p>{addr.postal_code}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-500">No addresses saved yet. Add one from your profile to unlock checkout.</p>
                )}
              </div>
              <Link to="/profile" className="mt-6 inline-flex items-center justify-center px-4 py-2 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:border-red-500">
                Manage addresses in Profile
              </Link>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold mb-4">Checkout</h2>
              <div className="space-y-2 mb-4">
                <div>Cart Total: ₹{cart?.total || 0}</div>
                <div>Discount: ₹{discount}</div>
                <div className="font-bold">Payable: ₹{cartTotal}</div>
              </div>
              <div className="flex gap-2 mb-4">
                <input value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="Coupon code" className="flex-1 border rounded p-2" />
                <button onClick={validateCoupon} className="bg-gray-200 px-4 rounded">Apply</button>
              </div>
              <button onClick={checkout} className="w-full bg-green-600 text-white py-3 rounded">Place COD Order</button>
            </div>
          </div>
        )}

        {tab === 'orders' && (
          <div className="bg-white rounded-3xl shadow p-6 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black text-slate-900">My Orders</h2>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Track purchases and status</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <select value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)} className="border border-slate-200 rounded-2xl px-4 py-2 text-sm">
                  {orderStatusOptions.map((status) => (
                    <option key={status} value={status}>{status === 'all' ? 'All statuses' : status}</option>
                  ))}
                </select>
                <input value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} placeholder="Search order #" className="border border-slate-200 rounded-2xl px-4 py-2 text-sm" />
              </div>
            </div>

            {filteredOrders.length === 0 && (
              <div className="text-center py-8 text-slate-500 border border-dashed rounded-2xl">
                No orders match your filters.
              </div>
            )}

            <div className="divide-y divide-slate-100">
              {filteredOrders.map((order) => {
                const placedDate = order.created_at || order.placed_at || order.createdAt;
                const formattedDate = placedDate ? new Date(placedDate).toLocaleDateString() : '—';
                return (
                  <div key={order.id} className="py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <p className="font-semibold text-slate-900">{order.order_number}</p>
                      <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{order.status}</p>
                      <p className="text-xs text-slate-500">Placed on {formattedDate}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-black text-slate-900">₹{order.total}</p>
                      <Link to={`/orders/${order.order_number}`} className="text-sm text-red-600 font-semibold">View details</Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === 'invoices' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">Invoices</h2>
            {invoices.map((invoice) => (
              <div key={invoice.id} className="border-b py-3 flex justify-between">
                <div>
                  <div className="font-medium">{invoice.invoice_number}</div>
                  <div className="text-sm text-gray-500">{invoice.issued_at}</div>
                </div>
                <div className="font-bold">₹{invoice.total}</div>
              </div>
            ))}
            {invoices.length === 0 && <div className="text-gray-500">No invoices yet.</div>}
          </div>
        )}

        {selectedProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold">{selectedProduct.name}</h3>
                <button onClick={() => setSelectedProduct(null)} className="text-gray-500">Close</button>
              </div>
              <p className="text-gray-600 mb-4">{selectedProduct.description}</p>
              <div className="font-bold text-xl mb-4">₹{selectedProduct.discount_price || selectedProduct.price}</div>

              {(selectedProduct.tags || []).length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {selectedProduct.tags.map((tag) => (
                    <span key={tag.id} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">{tag.name}</span>
                  ))}
                </div>
              )}

              {(selectedProduct.variants || []).length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Variants</h4>
                  <div className="space-y-2">
                    {selectedProduct.variants.map((variant) => (
                      <div key={variant.id} className="border rounded p-3 flex justify-between items-center">
                        <div>
                          <div className="font-medium">{variant.name}</div>
                          <div className="text-sm text-gray-500">SKU: {variant.sku}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">₹{variant.price}</div>
                          <div className="text-xs text-gray-500">Stock: {variant.stock}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(selectedProduct.offers || []).length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Offers</h4>
                  <div className="space-y-2">
                    {selectedProduct.offers.map((offer) => (
                      <div key={offer.id} className="bg-green-50 border border-green-200 rounded p-3">
                        <div className="font-medium text-green-800">{offer.title}</div>
                        <div className="text-sm text-green-700">{offer.type === 'percentage' ? `${offer.value}% off` : `₹${offer.value} off`}</div>
                        <div className="text-xs text-green-600">{offer.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <h4 className="font-semibold mb-2">Reviews</h4>
                {(selectedProduct.approved_reviews || []).length === 0 && <p className="text-gray-500 text-sm">No reviews yet.</p>}
                <div className="space-y-2">
                  {(selectedProduct.approved_reviews || []).map((review) => (
                    <div key={review.id} className="border rounded p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{review.user?.name || 'User'}</span>
                        <span className="text-yellow-500 text-sm">{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</span>
                      </div>
                      <p className="text-sm text-gray-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>

              {isAuthenticated && (
                <form onSubmit={submitReview} className="border rounded p-4 space-y-3">
                  <h4 className="font-semibold">Write a Review</h4>
                  <select value={reviewForm.rating} onChange={(e) => setReviewForm({ ...reviewForm, rating: Number(e.target.value) })} className="w-full border rounded p-2">
                    {[5, 4, 3, 2, 1].map((r) => (
                      <option key={r} value={r}>{r} Stars</option>
                    ))}
                  </select>
                  <textarea required placeholder="Your review" value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} className="w-full border rounded p-2" />
                  <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">Submit Review</button>
                </form>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default CustomerShop;
